/**
 * Headway and Frequency Calculation Utilities for Fixed Routes
 * 
 * These functions calculate headway (time between consecutive vehicles) and
 * frequency (vehicles per hour) for circulator routes based on loop duration
 * and the number of evenly spaced vehicles.
 */

/**
 * Calculate headway (time between consecutive vehicles) for a circulator route
 * 
 * Headway represents the maximum waiting time passengers will experience at any stop.
 * Formula: headway = loopDuration / vehicleCount
 * 
 * @param loopDurationMinutes - Total time for one complete loop (in minutes)
 * @param vehicleCount - Number of evenly spaced vehicles on the route
 * @returns Headway in minutes, or null if calculation cannot be performed
 * 
 * @example
 * // 45-minute loop with 3 vehicles = 15-minute headway
 * calculateHeadway(45, 3) // Returns 15
 */
export function calculateHeadway(
  loopDurationMinutes: number,
  vehicleCount: number
): number | null {
  // Validate inputs
  if (loopDurationMinutes <= 0 || vehicleCount <= 0) {
    return null;
  }
  
  // Check for invalid number types
  if (!Number.isFinite(loopDurationMinutes) || !Number.isFinite(vehicleCount)) {
    return null;
  }
  
  return loopDurationMinutes / vehicleCount;
}

/**
 * Calculate frequency (vehicles per hour) for a circulator route
 * 
 * Frequency represents how many vehicles pass a given stop per hour.
 * Formula: frequency = 60 / headway
 * 
 * @param headwayMinutes - Time between consecutive vehicles (in minutes)
 * @returns Frequency in vehicles per hour, or null if calculation cannot be performed
 * 
 * @example
 * // 15-minute headway = 4 vehicles per hour
 * calculateFrequency(15) // Returns 4.0
 */
export function calculateFrequency(
  headwayMinutes: number
): number | null {
  // Validate input
  if (headwayMinutes <= 0) {
    return null;
  }
  
  // Check for invalid number type
  if (!Number.isFinite(headwayMinutes)) {
    return null;
  }
  
  return 60 / headwayMinutes;
}

